import React, { useEffect, useState, useRef } from "react";
import { Layout, Row, Image, Spin, notification, message } from "antd";
import { Content } from "antd/lib/layout/layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SquarePayment from "../components/SquarePayment";
import { useServer } from "../contexts/Server";
import { useBrandContext } from "../contexts/Brand";
import {
  decodeParamString,
  encodeParamString,
  screenWidths,
} from "../const/const_functions";
import useWindowDimensions from "../hooks/useWindowDimensions";
import styles from "../styles/InvoicePayment.module.css";
import GlobalPaymentsCardEntry from "../components/GlobalPaymentsCardEntry";
import StripeCardEntryPayment from "../components/StripeCardEntryPayment";

const InvoicePayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serverData } = useServer();
  const { brandContextData } = useBrandContext();
  const paymentRef = useRef(null);

  const { encodedOrderId } = useParams();
  const [currOrder, setCurrOrder] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [paymentProcessor, setPaymentProcessor] = useState(null);
  const [formToken, setFormToken] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripePaymentStatus, setStripePaymentStatus] = useState('initial');
  const [stripeClientKey, setstripeClientKey] = useState(null);

  let mobile = false;
  const { width } = useWindowDimensions();
  if (screenWidths.md > width) {
    mobile = true;
  }

  useEffect(() => {
    if (paymentRef?.current) {
      paymentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  useEffect(() => {
    if (serverData) {
      if (location?.state?.orderID) {
        getOrderData(location.state.orderID);
        getPaymentProcessorInfo();
      } else {
        try {
          const orderId = decodeParamString(encodedOrderId);
          getOrderData(orderId);
          getPaymentProcessorInfo();
        } catch (e) {
          notification.error({
            message:
              "There was an error with retreiving the invoice. Please try creating it again.",
          });
        }
      }
    }
  }, [serverData]);

  const getOrderData = async (orderID) => {
    try {
      const res = await serverData.call("USSR_getOrderId", {
        orderId: orderID,
      });
      setCurrOrder(res);
    } catch (error) {
      console.log(error);
      notification.error({
        message:
          "There was an error with retreiving the invoice. Please try creating it again.",
      });
    }
  };

  const getPaymentProcessorInfo = async () => {
    try {
      const res = await serverData.call("MIKO_getPaymentProcessorAndFormToken", {
        contextId: brandContextData._id,
      });
      const { brandPaymentProcessor = 'SQ', formToken = null, brandSTCLK } = res;
      setPaymentProcessor(brandPaymentProcessor);

      if (brandPaymentProcessor === 'GP') {
        setFormToken(formToken);
      } else if (brandPaymentProcessor === 'STRIPE') {
        setstripeClientKey(brandSTCLK);
      }
    } catch (error) {
      console.log(error);
      notification.error({
        message:
          "There was an error with retreiving the invoice. Please try creating it again.",
      });
    }
  };

  const makeThePayment = async (token, verifiedBuyer, nextAction = null) => {
    setPlacingOrder(true);
    const { amount, _id, paymentInfo } = currOrder;
    let paymentXYZObj = {
      paymentXType: "xpayment-entered-card",
      nonce: token.token,
    };

    let serverPayment = null;

    try {
      serverPayment = await serverData.call("USSR_makePayment", {
        orderID: _id,
        userAppCalculatedOrder: amount,
        userAppTipped: 0,
        paymentXYZObj,
        verifiedBuyer,
        nextAction
      });
    } catch (error) {
      setPlacingOrder(false);
      console.log(error);
      if (error?.message) {
        message.error(error?.message);
      }
      if (paymentProcessor === 'STRIPE') {
        setStripePaymentStatus('failed');
      }
    }

    if (paymentProcessor === 'STRIPE' && serverPayment?.status === 'requires_action') {
      setPlacingOrder(false);
      setStripeClientSecret(serverPayment?.clientSecret);
      setStripePaymentStatus(serverPayment?.status);
      return;
    }

    const { paymentID } = serverPayment;
    const encodedOrderId = encodeParamString(_id);
    const imageUrl = currOrder?.stadium?.imageUrl
      ? currOrder.stadium.imageUrl
      : null;
    const newStoreUrl = `/${encodedOrderId}/invoiceconfirmation`;
    navigate(newStoreUrl, {
      state: {
        paymentID,
        total: amount,
        imageUrl,
        encodedStadiumId: location?.state?.encodedStadiumId
          ? location.state.encodedStadiumId
          : null,
        feeLabel: location?.state?.feeLabel ? location.state.feeLabel : null,
        orderAmount: paymentInfo.orderAmount,
        serviceFee: paymentInfo.merchantServiceFee,
      },
    });
    setPlacingOrder(false);
  };

  return (
    <Layout style={{ backgroundColor: "white", minHeight: "100vh" }}>
      <Spin tip="Loading..." spinning={placingOrder}>
        <Content
          ref={paymentRef}
          style={{
            marginTop: 20,
            marginBottom: 100,
            flexDirection: "column",
            display: "flex",
          }}
        >
          {currOrder?.stadium?.imageUrl ? (
            <Row
              justify={"center"}
              className={styles.logoContainer}
              style={{ paddingBottom: 50 }}
            >
              <Image
                width={mobile ? "50%" : 180}
                preview={false}
                src={currOrder.stadium.imageUrl}
                style={{
                  maxHeight: 180,
                  minHeight: 75,
                  objectFit: "contain",
                }}
              />
            </Row>
          ) : null}
          {currOrder ? (
            <>
              {paymentProcessor === 'SQ' && (
                <SquarePayment
                  makeThePayment={makeThePayment}
                  orderTotalWithEverything={currOrder.amount.toFixed(2)}
                  firstName={null}
                  lastName={null}
                  email={currOrder.orderedBy.email}
                  phoneNumber={null}
                />
              )}
              {paymentProcessor === 'GP' && (
                <GlobalPaymentsCardEntry
                  makeThePayment={makeThePayment}
                  formToken={formToken}
                  formType={"payment"}
                />
              )}
              {
                paymentProcessor === 'STRIPE' && (
                  <StripeCardEntryPayment
                    clientToken={stripeClientKey}
                    paymentCurrency={currOrder && currOrder.paymentInfo?.paymentCurrency ? String(currOrder.paymentInfo?.paymentCurrency) : 'CAD'}
                    orderTotalWithEverything={currOrder?.amount}
                    makeThePayment={makeThePayment}
                    clientSecret={stripeClientSecret}
                    paymentStatus={stripePaymentStatus}
                    maxWidth={false}
                  />
                )
              }
            </>
          ) : null}
        </Content>
      </Spin>
    </Layout>
  );
};

export default InvoicePayment;
